/**
 * 機材を選ぶ（SELECT GEAR）／機材（GEAR）／プリセット編集（GEAR EDIT）
 *
 * - 選択：カード（ラジオ・判型の枠・星）。選ぶと詳細が開く。下端「撮影を開始」
 * - 一覧：カードで編集、右上「新規」
 * - 編集：01 基本 / 02 フォーマットと向き / 03 レンズ / 04 フィルムと EI /
 *         05 フィルター / 06 現像液 / 07 既定のゾーン。下端「保存」、最下部に削除
 */

import {
  showScreen, goBack, toast, escapeHtml, icon, buzz, buildZoneScale, confirmDialog,
  barHtml, backBtn, zoneToRoman,
} from './ui.js';
import {
  listPresets, getPreset, createPreset, updatePreset, deletePreset,
  toggleFavorite, getActivePresetId, setActivePresetId, FILTER_FACTORS, duplicatePreset,
} from './presets.js';
import { FILMS, getFilmById } from './data/films.js';
import { DEVELOPERS, getDeveloperById } from './data/developers.js';
import { getAvailableDevelopersForFilm } from './data/dev-times.js';
import {
  get35mmEquivFocal, getHorizontalFovDegrees, calculateZoomFromPreset,
  parseFocalFromLensName, getDeviceBaseFov,
} from './data/film-formats.js';
import { ISOS } from './sekonic.js';
import { rangeOf } from './zone-calc.js';

export const FORMATS = [
  ['645', '6×4.5', 40, 30], ['66', '6×6', 32, 32], ['67', '6×7', 38, 32],
  ['68', '6×8', 42, 31], ['69', '6×9', 45, 30], ['617', '6×17', 62, 20],
];
export const FILTER_ABBR = { '': '', yellow8: 'Y2', orange16: 'O56', red25: 'R60', green11: 'G', polarizer: 'PL' };
const FILTER_ORDER = ['', 'yellow8', 'orange16', 'red25', 'green11', 'polarizer'];

export function formatLabel(fmt) {
  return FORMATS.find((f) => f[0] === fmt)?.[1] || fmt;
}
function frameSize(fmt) {
  const f = FORMATS.find((x) => x[0] === fmt) || FORMATS[2];
  return { w: 34, h: Math.round((34 * f[3]) / f[2]) };
}
function filterName(id) {
  const f = FILTER_FACTORS[id];
  return f && id ? f.name : 'なし';
}
export function presetMeta(p) {
  const film = getFilmById(p.filmId);
  const abbr = FILTER_ABBR[p.filterId];
  return [p.camera || 'Camera', film?.name || p.filmId, `EI ${p.ei}`, abbr].filter(Boolean).join(' · ');
}

// ==================================================
// SELECT GEAR
// ==================================================
export function openSelectGear() {
  renderSelectGear();
  showScreen('select-gear');
}

function renderSelectGear() {
  const el = document.getElementById('screen-select-gear');
  const presets = listPresets();
  let activeId = getActivePresetId();
  if (activeId && !getPreset(activeId)) activeId = null;
  const active = activeId ? getPreset(activeId) : null;

  el.innerHTML = `
    ${barHtml(backBtn(), `<button class="bar__text" type="button" data-new>${icon('plus', 16)}新規</button>`)}
    <div class="page-head"><div><div class="eyebrow">select gear</div><div class="page-title">機材を選ぶ</div></div></div>
    <div class="body">
      ${presets.length === 0 ? `
        <div class="empty"><div class="empty__t">プリセットがありません</div>
        <p class="empty__d">右上の「新規」から、お使いのカメラとフィルムの組合せを登録してください。</p>
        <button class="btn-line" type="button" data-new style="margin-top:14px">${icon('plus', 16)}新規プリセット</button></div>` : `
      <div class="cards">
        ${presets.map((p, i) => cardHtml(p, p.id === activeId, i, true)).join('')}
      </div>`}
    </div>
    <div class="foot">
      <div class="en sub" style="font-size:12px;padding:0 4px 10px">${active ? `選択中：${escapeHtml(active.name)} · ${escapeHtml(formatLabel(active.filmFormat))}${active.defaultLens ? ` · ${escapeHtml(active.defaultLens)}` : ''}` : 'プリセットを選ぶと撮影を開始できます'}</div>
      <button class="btn-primary btn-primary--next ${active ? '' : 'is-disabled'}" type="button" data-start style="height:64px">
        ${icon('lens', 24)}<span class="btn-primary__label" style="font-size:17px">撮影を開始</span>${icon('arrow')}
      </button>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:8px">
        <button class="btn-rule ${active ? '' : 'is-disabled'}" type="button" data-import>写真から読み込む</button>
        <button class="btn-rule ${active ? '' : 'is-disabled'}" type="button" data-nophoto>写真なしで記録</button>
      </div>
    </div>
  `;

  el.querySelectorAll('[data-new]').forEach((b) => b.addEventListener('click', () => openGearEdit(null, 'select-gear')));
  el.querySelectorAll('[data-preset-id]').forEach((card) => {
    card.addEventListener('click', (e) => {
      if (e.target.closest('[data-fav]')) return;
      setActivePresetId(card.dataset.presetId);
      buzz(6);
      // 選択の状態だけ切り替える（開閉のアニメーションを見せる）
      el.querySelectorAll('[data-preset-id]').forEach((c) => c.classList.toggle('is-on', c === card));
      updateSelectFoot(el);
    });
  });
  bindFavs(el, renderSelectGear);
  const withPreset = (fn) => () => {
    const preset = getPreset(getActivePresetId());
    if (!preset) { toast('プリセットを選択してください'); return; }
    import('./capture.js').then((m) => fn(m, preset));
  };
  el.querySelector('[data-import]').addEventListener('click', withPreset((m, p) => m.importFromPhotos(p)));
  el.querySelector('[data-nophoto]').addEventListener('click', withPreset((m, p) => m.recordWithoutPhoto(p)));
  el.querySelector('[data-start]').addEventListener('click', () => {
    const id = getActivePresetId();
    const preset = id ? getPreset(id) : null;
    if (!preset) { toast('プリセットを選択してください'); return; }
    import('./camera.js').then((m) => m.openViewfinder(preset));
  });
}

function updateSelectFoot(el) {
  const p = getPreset(getActivePresetId());
  const foot = el.querySelector('.foot .en');
  if (foot && p) foot.textContent = `選択中：${p.name} · ${formatLabel(p.filmFormat)}${p.defaultLens ? ` · ${p.defaultLens}` : ''}`;
  el.querySelector('[data-start]')?.classList.toggle('is-disabled', !p);
  el.querySelectorAll('[data-import], [data-nophoto]').forEach((b) => b.classList.toggle('is-disabled', !p));
}

function cardHtml(p, on, i, selectable) {
  const fr = frameSize(p.filmFormat);
  const equiv = get35mmEquivFocal(p.filmFormat, p.defaultLensFocalMm);
  const zoom = calculateZoomFromPreset(p, getDeviceBaseFov());
  const dev = getDeveloperById(p.defaultDeveloperId);
  const det = [
    ['レンズ', p.defaultLens ? `${p.defaultLens}${equiv ? ` → ${Math.round(equiv)}mm 相当` : ''}` : '—'],
    ['iPhone', p.defaultLensFocalMm ? `${zoom.toFixed(2)}× ズーム` : 'ズームなし'],
    ['フィルター', filterName(p.filterId)],
    ['現像液', dev ? `${dev.name} ${dev.dilution}` : '—'],
    ['シャドウ', zoneToRoman(p.defaultShadowZone ?? 3)],
    ['ハイライト', zoneToRoman(p.defaultHighlightZone ?? 8)],
  ];
  return `
    <div class="pcard ${on ? 'is-on' : ''}" data-preset-id="${escapeHtml(p.id)}" style="animation-delay:${i * 70}ms" role="button" tabindex="0">
      <div class="pcard__row">
        ${selectable ? '<span class="radio"><span></span></span>' : ''}
        <div class="pcard__body"><div class="pcard__name">${escapeHtml(p.name)}</div><div class="pcard__meta">${escapeHtml(presetMeta(p))}</div></div>
        <div class="pcard__fmt"><span class="pcard__frame" style="width:${fr.w}px;height:${fr.h}px"></span>${escapeHtml(formatLabel(p.filmFormat))}</div>
        <button class="star" type="button" data-fav="${escapeHtml(p.id)}" aria-label="お気に入り">${icon(p.isFavorite ? 'star-fill' : 'star')}</button>
        ${selectable ? '' : `<span style="color:var(--color-muted);padding-right:8px">${icon('chev', 16)}</span>`}
      </div>
      ${selectable ? `<div class="pcard__det"><div><div class="pcard__grid">
        ${det.map(([k, v]) => `<div><div class="kv__k">${k}</div><div class="kv__v">${escapeHtml(v)}</div></div>`).join('')}
      </div></div></div>` : ''}
    </div>`;
}

function bindFavs(el, rerender) {
  el.querySelectorAll('[data-fav]').forEach((b) => {
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleFavorite(b.dataset.fav);
      buzz(6);
      rerender();
    });
  });
}

// ==================================================
// GEAR（一覧）
// ==================================================
export function openGear() {
  renderGearList();
  showScreen('gear');
}

function renderGearList() {
  const el = document.getElementById('screen-gear');
  const presets = listPresets();
  el.innerHTML = `
    ${barHtml(backBtn(), `<button class="bar__text" type="button" data-new>${icon('plus', 16)}新規</button>`)}
    <div class="page-head"><div><div class="eyebrow">gear presets</div><div class="page-title">機材</div></div></div>
    <div class="body">
      ${presets.length === 0 ? `
        <div class="empty"><div class="empty__t">プリセットがありません</div>
        <p class="empty__d">右上の「新規」から登録できます。</p></div>` : `
      <div class="cards">${presets.map((p, i) => cardHtml(p, false, i, false)).join('')}</div>
      <p class="sub" style="margin:6px var(--gutter) 40px;font-size:12px;line-height:1.7">お気に入りが上に、その中は更新の新しい順に並びます。</p>`}
    </div>
  `;
  el.querySelectorAll('[data-new]').forEach((b) => b.addEventListener('click', () => openGearEdit(null, 'gear')));
  el.querySelectorAll('[data-preset-id]').forEach((card) => {
    card.addEventListener('click', (e) => {
      if (e.target.closest('[data-fav]')) return;
      openGearEdit(card.dataset.presetId, 'gear');
    });
  });
  bindFavs(el, renderGearList);
}

// ==================================================
// GEAR EDIT
// ==================================================
let ge = null; // 編集中の値
let returnTo = 'gear';

export function openGearEdit(id, from = 'gear') {
  returnTo = from;
  const p = id ? getPreset(id) : null;
  const filmId = p?.filmId || 'tri-x-400';
  ge = {
    id: p?.id || null,
    name: p?.name || '',
    camera: p?.camera || '',
    filmFormat: p?.filmFormat || '67',
    defaultOrientation: p?.defaultOrientation || 'auto',
    defaultLens: p?.defaultLens || '',
    focal: p?.defaultLensFocalMm != null ? String(p.defaultLensFocalMm) : '',
    filmId,
    ei: p?.ei ?? getFilmById(filmId)?.iso ?? 400,
    filterId: p?.filterId || '',
    defaultDeveloperId: p?.defaultDeveloperId || 'd76-1+1',
    shadow: p?.defaultShadowZone ?? 3,
    highlight: p?.defaultHighlightZone ?? 8,
    filmOpen: false,
    devOpen: false,
    // 4. 露出の範囲。触っていなければ null のまま保存し、全範囲で計算する（旧プリセットと同じ）
    expo: p?.expoRange ? { ...p.expoRange } : null,
  };
  renderGearEdit();
  showScreen('gear-edit');
}

function renderGearEdit() {
  const el = document.getElementById('screen-gear-edit');
  const isNew = !ge.id;
  el.innerHTML = `
    ${barHtml(backBtn())}
    <div class="page-head"><div><div class="eyebrow">${isNew ? 'new preset' : 'edit preset'}</div><div class="page-title">${isNew ? '新規プリセット' : 'プリセットを編集'}</div></div></div>
    <div class="body"><div style="padding:0 var(--gutter) 28px">
      <div class="sec-head" style="margin-top:6px"><span>01 basic</span><span class="sec-head__jp">基本</span></div>
      <label class="field"><span class="field__label">プリセット名</span><input class="input" id="ge-name" value="${escapeHtml(ge.name)}" placeholder="例：Pentax 67 / Tri-X" /></label>
      <label class="field"><span class="field__label">カメラ</span><input class="input en" id="ge-camera" value="${escapeHtml(ge.camera)}" placeholder="例：Pentax 67" /></label>

      <div class="sec-head"><span>02 format</span><span class="sec-head__jp">フォーマットと向き</span></div>
      <div class="fmt-grid" id="ge-fmts">
        ${FORMATS.map(([v, t, w, h]) => `<button type="button" class="fmt-opt ${ge.filmFormat === v ? 'is-on' : ''}" data-fmt="${v}"><span style="width:${w}px;height:${h}px"></span><span>${t}</span></button>`).join('')}
      </div>
      <div class="field__label" style="margin:18px 0 0">既定の向き</div>
      <div class="seg" style="margin-top:10px" id="ge-orient">
        ${[['auto', '自動'], ['portrait', '縦'], ['landscape', '横']].map(([v, t]) => `<button type="button" class="seg__opt ${ge.defaultOrientation === v ? 'is-on' : ''}" data-orient="${v}">${t}</button>`).join('')}
      </div>

      <div class="sec-head"><span>03 lens</span><span class="sec-head__jp">レンズ</span></div>
      <label class="field"><span class="field__label">既定レンズ名</span><input class="input en" id="ge-lens" value="${escapeHtml(ge.defaultLens)}" placeholder="例：SMC Takumar 105/2.4" /></label>
      <label class="field"><span class="field__label">焦点距離 (mm)</span><input class="input en" id="ge-focal" inputmode="numeric" value="${escapeHtml(ge.focal)}" placeholder="20〜2000（レンズ名から自動入力）" /></label>
      <div class="eqbox" id="ge-eq"></div>

      <div class="sec-head"><span>04 film</span><span class="sec-head__jp">フィルムと EI</span></div>
      <div class="field"><span class="field__label">フィルム</span>
        <div class="picker ${ge.filmOpen ? 'is-open' : ''}" id="ge-film">
          <button class="picker__btn" type="button" data-toggle><span>${escapeHtml(filmLabel(ge.filmId))}</span>${icon('down', 16)}</button>
          <div class="picker__list"><div>${filmOptions()}</div></div>
        </div>
      </div>
      <div class="field__label" style="margin:14px 0 8px">個人 EI</div>
      <div class="stepper">
        <button class="stepper__b" type="button" data-ei="-1" aria-label="EI を下げる">${icon('minus', 18)}</button>
        <input class="stepper__v" id="ge-ei" inputmode="numeric" value="${escapeHtml(String(ge.ei))}" />
        <button class="stepper__b" type="button" data-ei="1" aria-label="EI を上げる">${icon('plus', 16)}</button>
      </div>
      <div class="sub" style="font-size:12px;line-height:1.5;margin-top:8px" id="ge-ei-note"></div>

      <div class="sec-head"><span>05 filter</span><span class="sec-head__jp">既定フィルター</span></div>
      <div class="filter-grid">
        ${FILTER_ORDER.map((v) => `<button type="button" class="filter-opt ${ge.filterId === v ? 'is-on' : ''}" data-filter="${v}"><span>${escapeHtml(v ? FILTER_FACTORS[v].name : 'なし')}</span><span>${FILTER_ABBR[v]}</span></button>`).join('')}
      </div>

      <div class="sec-head"><span>06 developer</span><span class="sec-head__jp">既定の現像液</span></div>
      <div class="field"><span class="field__label">現像液</span>
        <div class="picker ${ge.devOpen ? 'is-open' : ''}" id="ge-dev">
          <button class="picker__btn" type="button" data-toggle><span>${escapeHtml(devLabel(ge.defaultDeveloperId))}</span>${icon('down', 16)}</button>
          <div class="picker__list"><div>${devOptions(ge.filmId, ge.defaultDeveloperId)}</div></div>
        </div>
      </div>

      <div class="sec-head"><span>07 zones</span><span class="sec-head__jp">既定のゾーン</span></div>
      <div style="display:flex;justify-content:space-between;margin:16px 0 10px;font:500 13px/1 var(--font-jp-sans)"><span>シャドウ</span><span class="en" id="ge-s-r">${zoneToRoman(ge.shadow)}</span></div>
      <div id="ge-s" style="height:44px"></div>
      <div style="display:flex;justify-content:space-between;margin:20px 0 10px;font:500 13px/1 var(--font-jp-sans)"><span>ハイライト</span><span class="en" id="ge-h-r">${zoneToRoman(ge.highlight)}</span></div>
      <div id="ge-h" style="height:44px"></div>
      <div class="sub" style="font-size:12px;line-height:1.5;margin-top:12px">測光ピンの 1 本目・2 本目の初期ゾーンになります。指でなぞって選べます。</div>

      <div class="sec-head"><span>08 exposure range</span><span class="sec-head__jp">露出の範囲</span></div>
      <p class="sub" style="font-size:12px;line-height:1.6;margin:10px 0 0">露出の組合せを、このカメラとレンズで切れる値の中から出します。${ge.expo ? '' : '未設定のときは 1/1000〜60 秒・f/2.8〜45 で計算します。'}</p>
      <div id="ge-expo"></div>

      ${isNew ? '' : `<button class="btn-line btn-line--block" type="button" data-duplicate style="margin-top:32px;height:52px">${icon('plus', 18)}このプリセットを複製</button>
      <button class="btn-line btn-line--block" type="button" data-delete style="margin-top:8px;height:52px">${icon('trash', 18)}このプリセットを削除</button>`}
    </div></div>
    <div class="foot"><button class="btn-primary" type="button" data-save style="height:58px">保存</button></div>
  `;

  const $ = (s) => el.querySelector(s);
  $('#ge-name').addEventListener('input', (e) => { ge.name = e.target.value; });
  $('#ge-camera').addEventListener('input', (e) => { ge.camera = e.target.value; });
  $('#ge-lens').addEventListener('input', (e) => {
    ge.defaultLens = e.target.value;
    const wide = parseMaxAperture(ge.defaultLens);
    if (wide) {
      ge.expo = { ...rangeOf({ expoRange: ge.expo }), ...(ge.expo || {}), widest: wide };
      renderExpoRange(el);
    }
    const f = parseFocalFromLensName(ge.defaultLens);
    if (f && !ge.focal) {
      ge.focal = String(f);
      $('#ge-focal').value = ge.focal;
      renderEq(el);
    }
  });
  $('#ge-focal').addEventListener('input', (e) => {
    ge.focal = e.target.value.replace(/[^0-9]/g, '');
    e.target.value = ge.focal;
    renderEq(el);
  });
  el.querySelectorAll('[data-fmt]').forEach((b) => b.addEventListener('click', () => {
    ge.filmFormat = b.dataset.fmt;
    buzz(6);
    el.querySelectorAll('[data-fmt]').forEach((x) => x.classList.toggle('is-on', x === b));
    renderEq(el);
  }));
  el.querySelectorAll('[data-orient]').forEach((b) => b.addEventListener('click', () => {
    ge.defaultOrientation = b.dataset.orient;
    el.querySelectorAll('[data-orient]').forEach((x) => x.classList.toggle('is-on', x === b));
  }));
  el.querySelectorAll('[data-filter]').forEach((b) => b.addEventListener('click', () => {
    ge.filterId = b.dataset.filter;
    buzz(6);
    el.querySelectorAll('[data-filter]').forEach((x) => x.classList.toggle('is-on', x === b));
  }));
  bindPicker($('#ge-film'), 'filmOpen', (v) => {
    ge.filmId = v;
    ge.ei = getFilmById(v)?.iso ?? ge.ei;
    ge.filmOpen = false;
    renderGearEdit();
  });
  bindPicker($('#ge-dev'), 'devOpen', (v) => {
    ge.defaultDeveloperId = v;
    ge.devOpen = false;
    renderGearEdit();
  });
  el.querySelectorAll('[data-ei]').forEach((b) => b.addEventListener('click', () => {
    const cur = Number(ge.ei);
    let i = ISOS.indexOf(cur);
    if (i < 0) i = ISOS.findIndex((v) => v >= cur);
    if (i < 0) i = ISOS.length - 1;
    ge.ei = ISOS[Math.max(0, Math.min(ISOS.length - 1, i + Number(b.dataset.ei)))];
    $('#ge-ei').value = String(ge.ei);
    renderEiNote(el);
  }));
  $('#ge-ei').addEventListener('input', (e) => {
    const v = parseInt(e.target.value, 10);
    if (Number.isFinite(v)) { ge.ei = v; renderEiNote(el); }
  });
  $('#ge-ei').addEventListener('blur', (e) => { ge.ei = clampEI(parseInt(e.target.value, 10)); e.target.value = String(ge.ei); renderEiNote(el); });

  buildZoneScale($('#ge-s'), ge.shadow, (z) => { ge.shadow = z; $('#ge-s-r').textContent = zoneToRoman(z); });
  $('#ge-s').style.height = '44px';
  buildZoneScale($('#ge-h'), ge.highlight, (z) => { ge.highlight = z; $('#ge-h-r').textContent = zoneToRoman(z); });
  $('#ge-h').style.height = '44px';

  renderExpoRange(el);
  $('[data-save]').addEventListener('click', () => saveGearEdit());
  $('[data-duplicate]')?.addEventListener('click', () => {
    // 編集中の内容を保存してから複製し、複製した方の編集を開く
    saveGearEdit({ silent: true });
    const copy = duplicatePreset(ge.id);
    if (!copy) return;
    if (returnTo === 'select-gear') renderSelectGear(); else renderGearList();
    toast('複製しました。名前などを変えて保存してください');
    openGearEdit(copy.id, returnTo);
  });
  $('[data-delete]')?.addEventListener('click', async () => {
    const ok = await confirmDialog({ title: 'このプリセットを削除しますか？', body: '選択中のプリセットだった場合、選択も解除されます。', ok: '削除' });
    if (!ok) return;
    deletePreset(ge.id);
    toast('プリセットを削除しました');
    afterSave();
  });

  renderEq(el);
  renderEiNote(el);
}

const FASTEST = [[1 / 400, '1/400'], [1 / 500, '1/500'], [1 / 1000, '1/1000'], [1 / 2000, '1/2000']];
const SLOWEST = [[1, '1 秒'], [8, '8 秒'], [60, '60 秒']];
const SMALLEST = [16, 22, 32, 45];

/** 4. 露出の範囲の入力 */
function renderExpoRange(el) {
  const box = el.querySelector('#ge-expo');
  // 範囲が未設定なら、開放絞りはレンズ名から読む（読めなければ f/2.8）
  const R = rangeOf({ expoRange: ge.expo || { widest: parseMaxAperture(ge.defaultLens) || undefined } });
  const seg = (key, opts, cur, fmt = (x) => x) => `<div class="seg" style="margin-top:8px">${opts.map(([v, t]) => `<button type="button" class="seg__opt en ${Math.abs(v - cur) < 1e-6 ? 'is-on' : ''}" data-${key}="${v}">${fmt(t)}</button>`).join('')}</div>`;
  box.innerHTML = `
    <div class="field__label" style="margin:14px 0 0">最速のシャッター速度</div>${seg('fast', FASTEST, R.fastest)}
    <div class="field__label" style="margin:14px 0 0">最も遅いシャッター速度（目盛りのある範囲）</div>${seg('slow', SLOWEST, R.slowest)}
    <div style="display:grid;grid-template-columns:1fr 1.6fr;gap:10px;margin-top:14px;align-items:end">
      <label><span class="field__label">開放絞り f/</span><input class="input en" id="ge-wide" inputmode="decimal" value="${R.widest}" /></label>
      <div><span class="field__label">最小絞り f/</span>${seg('small', SMALLEST.map((v) => [v, String(v)]), R.smallest)}</div>
    </div>
    ${ge.expo ? '<button type="button" data-expo-clear class="sub" style="margin-top:10px;font:500 12px/1 var(--font-jp-sans);text-decoration:underline;text-underline-offset:3px;padding:6px 0">範囲の設定を外す（全範囲で計算）</button>' : ''}`;
  const set = (patch) => { ge.expo = { ...R, ...(ge.expo || {}), ...patch }; buzz(6); renderExpoRange(el); };
  box.querySelectorAll('[data-fast]').forEach((b) => b.addEventListener('click', () => set({ fastest: Number(b.dataset.fast) })));
  box.querySelectorAll('[data-slow]').forEach((b) => b.addEventListener('click', () => set({ slowest: Number(b.dataset.slow) })));
  box.querySelectorAll('[data-small]').forEach((b) => b.addEventListener('click', () => set({ smallest: Number(b.dataset.small) })));
  box.querySelector('#ge-wide').addEventListener('change', (e) => {
    const v = parseFloat(e.target.value);
    if (Number.isFinite(v) && v >= 1 && v <= 16) set({ widest: v }); else e.target.value = String(R.widest);
  });
  box.querySelector('[data-expo-clear]')?.addEventListener('click', () => { ge.expo = null; renderExpoRange(el); });
}

/** レンズ名から開放絞りを読む（"105/2.4" "80mm f2.8" "1:2.8"） */
function parseMaxAperture(name) {
  const m = String(name || '').match(/(?:\/|f\/?|1:)\s*(\d{1,2}(?:\.\d)?)(?!\d)/i);
  if (!m) return null;
  const v = parseFloat(m[1]);
  return v >= 1 && v <= 16 ? v : null;
}

function bindPicker(root, key, onPick) {
  root.querySelector('[data-toggle]').addEventListener('click', () => {
    ge[key] = !ge[key];
    root.classList.toggle('is-open', ge[key]);
  });
  root.querySelectorAll('[data-v]').forEach((b) => b.addEventListener('click', () => { buzz(6); onPick(b.dataset.v); }));
}

function filmLabel(id) {
  const f = getFilmById(id);
  return f ? `${f.brand} ${f.name} · ISO ${f.iso}` : id;
}
function devLabel(id) {
  const d = getDeveloperById(id);
  return d ? `${d.name} ${d.dilution}` : id;
}
function groupBy(arr, key) {
  const m = new Map();
  arr.forEach((x) => { if (!m.has(x[key])) m.set(x[key], []); m.get(x[key]).push(x); });
  return m;
}
function filmOptions() {
  const out = [];
  for (const [brand, films] of groupBy(FILMS, 'brand')) {
    out.push(`<div class="picker__group">${escapeHtml(brand)}</div>`);
    films.forEach((f) => {
      const color = /portra|velvia|ektar/.test(f.id);
      out.push(`<button type="button" class="picker__opt ${f.id === ge.filmId ? 'is-on' : ''}" data-v="${escapeHtml(f.id)}"><span>${escapeHtml(f.name)}</span><span class="picker__note">ISO ${f.iso}${color ? ' · カラー参考' : ''}</span></button>`);
    });
  }
  return out.join('');
}
export function devOptions(filmId, currentId) {
  const available = new Set(getAvailableDevelopersForFilm(filmId));
  const out = [];
  for (const [brand, devs] of groupBy(DEVELOPERS, 'brand')) {
    out.push(`<div class="picker__group">${escapeHtml(brand)}</div>`);
    devs.forEach((d) => {
      out.push(`<button type="button" class="picker__opt ${d.id === currentId ? 'is-on' : ''}" data-v="${escapeHtml(d.id)}"><span>${escapeHtml(`${d.name} ${d.dilution}`)}</span><span class="picker__note">${available.has(d.id) ? '' : '標準時間なし'}</span></button>`);
    });
  }
  return out.join('');
}

function renderEq(el) {
  const box = el.querySelector('#ge-eq');
  const focal = parseInt(ge.focal, 10);
  const base = getDeviceBaseFov();
  if (!Number.isFinite(focal) || focal < 20 || focal > 2000) {
    box.innerHTML = `
      <div><div class="eqbox__k">35mm eq.</div><div class="eqbox__v">—</div></div>
      <div><div class="eqbox__k">h. fov</div><div class="eqbox__v">—</div></div>
      <div><div class="eqbox__k">iphone</div><div class="eqbox__v">—</div></div>
      <div class="eqbox__note">未指定（iPhone 撮影時のズーム自動調整なし）</div>`;
    return;
  }
  const equiv = get35mmEquivFocal(ge.filmFormat, focal);
  const fov = getHorizontalFovDegrees(ge.filmFormat, focal);
  const zoom = calculateZoomFromPreset({ filmFormat: ge.filmFormat, defaultLensFocalMm: focal }, base);
  box.innerHTML = `
    <div><div class="eqbox__k">35mm eq.</div><div class="eqbox__v">${Math.round(equiv)}mm</div></div>
    <div><div class="eqbox__k">h. fov</div><div class="eqbox__v">${fov.toFixed(1)}°</div></div>
    <div><div class="eqbox__k">iphone</div><div class="eqbox__v">${zoom.toFixed(2)}×</div></div>
    <div class="eqbox__note">基準 ${base}mm（設定で変更）。撮影時にこの倍率でズームします。</div>`;
}

function renderEiNote(el) {
  const box = getFilmById(ge.filmId)?.iso || 400;
  const ei = Number(ge.ei);
  const note = el.querySelector('#ge-ei-note');
  if (!Number.isFinite(ei) || ei <= 0) { note.textContent = ''; return; }
  const thirds = Math.round(Math.log2(ei / box) * 3);
  note.textContent = thirds === 0
    ? '箱の感度どおり。フィルムを変えると EI は箱の感度に戻ります。'
    : `箱の感度 ISO ${box} から ${thirds > 0 ? '+' : '−'}${Math.abs(thirds)}/3 段。フィルムを変えると EI は箱の感度に戻ります。`;
}

function clampEI(v) {
  if (!Number.isFinite(v)) return 400;
  return Math.max(6, Math.min(6400, Math.round(v)));
}

function saveGearEdit(opts = {}) {
  const focalRaw = parseInt(ge.focal, 10);
  const focal = Number.isFinite(focalRaw) && focalRaw >= 20 && focalRaw <= 2000 ? focalRaw : null;
  const data = {
    name: ge.name.trim() || 'Untitled',
    camera: ge.camera.trim(),
    filmFormat: ge.filmFormat,
    defaultOrientation: ge.defaultOrientation,
    defaultLens: ge.defaultLens.trim(),
    defaultLensFocalMm: focal,
    filmId: ge.filmId,
    ei: clampEI(Number(ge.ei)),
    filterId: ge.filterId,
    defaultDeveloperId: ge.defaultDeveloperId,
    defaultShadowZone: ge.shadow,
    defaultHighlightZone: ge.highlight,
    expoRange: ge.expo || null,
  };
  if (opts.silent) {
    // 複製の前の保存：画面は移らない
    if (ge.id) updatePreset(ge.id, data);
    return;
  }
  if (ge.id) {
    updatePreset(ge.id, data);
    toast('保存しました');
  } else {
    const created = createPreset(data);
    if (returnTo === 'select-gear') setActivePresetId(created.id);
    toast('プリセットを作成しました');
  }
  afterSave();
}

function afterSave() {
  if (returnTo === 'select-gear') {
    renderSelectGear();
  } else {
    renderGearList();
  }
  goBack();
}
